import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Handle, Position } from "@xyflow/react";
import {
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import MessageTemplateService from "../../services/MessageTemplateService";

export const TemplateMessageNode = ({ data, id, selected = false }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const menuRef = useRef(null);

  const [approvedTemplates, setApprovedTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(data.templateId || "");
  const [templateVariables, setTemplateVariables] = useState(
    data.variables && typeof data.variables === "object" && !Array.isArray(data.variables)
      ? data.variables
      : { header: {}, body: {}, footer: {} }
  );

  useEffect(() => {
    setSelectedTemplateId(data.templateId || "");
    setTemplateVariables(
      data.variables && typeof data.variables === "object" && !Array.isArray(data.variables)
        ? { header: {}, body: {}, footer: {}, ...data.variables }
        : { header: {}, body: {}, footer: {} }
    );
  }, [data.templateId, data.variables]);

  useEffect(() => {
    MessageTemplateService.getAllTemplates().then((res) => setApprovedTemplates(res || []));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  const selectedTemplate = approvedTemplates.find((t) => t.getId() === selectedTemplateId);
  let variableSamples = { header: {}, body: {}, footer: {} };
  try {
    variableSamples = selectedTemplate
      ? (typeof selectedTemplate.attrs.variableSamples === "string"
          ? JSON.parse(selectedTemplate.attrs.variableSamples)
          : selectedTemplate.attrs.variableSamples || variableSamples)
      : variableSamples;
  } catch (e) {}

  const handleEdit = () => {
    setSelectedTemplateId(data.templateId || "");
    setTemplateVariables(
      data.variables && typeof data.variables === "object" && !Array.isArray(data.variables)
        ? { header: {}, body: {}, footer: {}, ...data.variables }
        : { header: {}, body: {}, footer: {} }
    );
    setShowEditPopup(true);
    setShowMenu(false);
  };

  const handleTemplateChange = (value) => {
    setSelectedTemplateId(value);
    const tpl = approvedTemplates.find((t) => t.getId() === value);
    let samples = { header: {}, body: {}, footer: {} };
    try {
      samples = tpl
        ? (typeof tpl.attrs.variableSamples === "string"
            ? JSON.parse(tpl.attrs.variableSamples)
            : tpl.attrs.variableSamples || samples)
        : samples;
    } catch (e) {}
    setTemplateVariables({
      header: Object.fromEntries(Object.keys(samples.header || {}).map((k) => [k, ""])),
      body: Object.fromEntries(Object.keys(samples.body || {}).map((k) => [k, ""])),
      footer: Object.fromEntries(Object.keys(samples.footer || {}).map((k) => [k, ""])),
    });
  };

  const handleSave = () => {
    if (data.onChangeTemplate) data.onChangeTemplate(id, selectedTemplateId || null);
    if (data.onChangeVariables) data.onChangeVariables(id, templateVariables);
    setShowEditPopup(false);
    setShowMenu(false);
  };

  const handleCancel = () => {
    setSelectedTemplateId(data.templateId || "");
    setTemplateVariables(
      data.variables && typeof data.variables === "object" && !Array.isArray(data.variables)
        ? { header: {}, body: {}, footer: {}, ...data.variables }
        : { header: {}, body: {}, footer: {} }
    );
    setShowEditPopup(false);
    setShowMenu(false);
  };

  const handleDelete = () => {
    if (data.onDelete) {
      data.onDelete(id);
    }
    setShowMenu(false);
  };

  const menuStyle = {
    position: "absolute",
    top: 30,
    right: 8,
    background: "white",
    border: "1px solid #ddd",
    borderRadius: "4px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    zIndex: 2,
    minWidth: "100px",
  };

  const menuItemStyle = {
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "12px",
    borderBottom: "1px solid #eee",
    transition: "background-color 0.2s",
  };

  const popupOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  };

  const popupStyle = {
    background: "white",
    borderRadius: "8px",
    padding: "0",
    width: "600px",
    maxHeight: "80vh",
    overflowY: "auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
  };

  const displayTemplate = approvedTemplates.find((t) => t.getId() === data.templateId);
  const displayBody = displayTemplate ? (displayTemplate.getBody && displayTemplate.getBody()) || "" : "";
  const displayName = displayTemplate ? (displayTemplate.getTemplateName && displayTemplate.getTemplateName()) || "" : "";

  return (
    <>
      <div
        style={{
          background: selected ? "#f8f9fa" : "#f1f3f4",
          border: `1px solid ${selected ? "#1976d2" : "#e0e0e0"}`,
          borderRadius: "6px",
          padding: "8px",
          width: "160px",
          minHeight: "60px",
          boxShadow: selected
            ? "0 4px 8px rgba(25, 118, 210, 0.3)"
            : "0 2px 4px rgba(0,0,0,0.1)",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 8,
            cursor: "pointer",
            fontSize: 16,
            color: selected ? "#1976d2" : "#6c757d",
            zIndex: 1,
            userSelect: "none",
            fontWeight: "bold",
            lineHeight: "1",
            display: "flex",
            alignItems: "center",
            height: "11px",
          }}
          onClick={() => setShowMenu(!showMenu)}
          title="Menu"
        >
          ⋮
        </div>

        {showMenu && (
          <div ref={menuRef} style={menuStyle}>
            <div
              style={menuItemStyle}
              onClick={handleEdit}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
            >
              ✏️ Edit
            </div>
            <div
              style={{ ...menuItemStyle, borderBottom: "none" }}
              onClick={handleDelete}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
            >
              🗑️ Delete
            </div>
          </div>
        )}

        <Handle
          type="target"
          position={Position.Left}
          style={{ background: selected ? "#1976d2" : "#6c757d" }}
        />
        <div style={{ textAlign: "left" }}>
          <div
            style={{
              fontWeight: "bold",
              color: selected ? "#1976d2" : "#6c757d",
              marginBottom: "8px",
              fontSize: "11px",
            }}
          >
            Template Message
          </div>

          {data.templateId && displayName ? (
            <div style={{ fontSize: "14px", color: "#666", marginBottom: "8px" }}>
              <div style={{ cursor: "default", minHeight: "40px", textAlign: "left" }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {displayName}
                </Typography>
                {displayBody && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {displayBody.length > 60 ? `${displayBody.slice(0, 60)}...` : displayBody}
                  </Typography>
                )}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "8px" }}>
              <div style={{ cursor: "default", fontStyle: "italic", textAlign: "left" }}>
                Click Edit to select template and set variables
              </div>
            </div>
          )}
        </div>
      </div>

      {showEditPopup &&
        createPortal(
          <>
            <div style={popupOverlayStyle} onClick={handleCancel}>
              <div style={popupStyle} onClick={(e) => e.stopPropagation()}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "24px 24px 16px 24px",
                    borderBottom: "1px solid #e9ecef",
                    position: "sticky",
                    top: 0,
                    backgroundColor: "white",
                    zIndex: 1,
                    borderRadius: "8px 8px 0 0",
                  }}
                >
                  <h3 style={{ margin: 0, color: "#333", fontSize: "18px", fontWeight: "bold" }}>
                    Template Message
                  </h3>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <button
                      onClick={handleSave}
                      style={{
                        padding: "6px 12px",
                        border: "1px solid #28a745",
                        borderRadius: "4px",
                        backgroundColor: "#28a745",
                        color: "white",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancel}
                      style={{
                        padding: "6px 8px",
                        border: "1px solid #6c757d",
                        borderRadius: "4px",
                        backgroundColor: "white",
                        color: "#6c757d",
                        cursor: "pointer",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "28px",
                        height: "28px",
                      }}
                      title="Close"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <div style={{ padding: "0 24px 24px 24px" }}>
                  <FormControl fullWidth size="small" sx={{ mt: 2, mb: 2 }}>
                    <InputLabel>Select Template</InputLabel>
                    <Select
                      value={selectedTemplateId}
                      onChange={(e) => handleTemplateChange(e.target.value)}
                      label="Select Template"
                      MenuProps={{
                        sx: { zIndex: 10001 },
                        disableScrollLock: true,
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {approvedTemplates.map((template) => (
                        <MenuItem key={template.getId()} value={template.getId()}>
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {template.getTemplateName()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {template.getCategory()} • {template.getLanguage()}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedTemplate && (
                    <>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                        Template body (preview)
                      </Typography>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: "#f5f5f5",
                          borderRadius: 1,
                          border: "1px solid #e0e0e0",
                          minHeight: "60px",
                          mb: 2,
                          fontSize: "14px",
                          color: "#333",
                        }}
                      >
                        {selectedTemplate.getBody() || "—"}
                      </Box>

                      {["header", "body", "footer"].map(
                        (section) =>
                          variableSamples[section] &&
                          Object.keys(variableSamples[section]).length > 0 && (
                            <Box
                              key={section}
                              sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1, mb: 2 }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                {section.charAt(0).toUpperCase() + section.slice(1)} variables
                              </Typography>
                              {Object.keys(variableSamples[section]).map((key) => (
                                <TextField
                                  key={key}
                                  label={`Value for {{${key}}}`}
                                  value={templateVariables[section]?.[key] ?? ""}
                                  onChange={(e) =>
                                    setTemplateVariables((prev) => ({
                                      ...prev,
                                      [section]: {
                                        ...prev[section],
                                        [key]: e.target.value,
                                      },
                                    }))
                                  }
                                  fullWidth
                                  size="small"
                                />
                              ))}
                            </Box>
                          )
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};
